import { useMemo, useEffect, useCallback, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useNavigate } from 'hooks'
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil'
import {
  selectedProductsAtom,
  selectedThemeAtom,
  customizationAtom,
  orderAtom,
  themesAtom,
  pinnedThemesAtom,
  customThemesAtom,
  productsAtom,
  caseAtom,
  applicationTypeAtom,
  themesArrayAtom,
  currentProductIdAtom,
} from 'atoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
import { Button, Link, NavItem, LogoutButton } from 'components'
import Flex from 'components/priority/Flex/Flex'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import DesignerGate from 'components/auth/DesignerGate'
import { useTranslation } from 'react-i18next'
import LanguageSelector from 'components/global/LanguageSelector/LanguageSelector'
import {
  isDesignerTokenSession,
  isQcTokenSession,
} from 'utils/global/designerSession'
import { M3, MDL } from '@messenger/consts'
import { getAllProducts } from '@/api/products'
import { getPinnedThemes, getThemeCategories } from '@/api/themes'
import { getCustomThemes } from '@/api/customThemes'
import { getCase as getCaseApi } from '@/api/cases'
import { getCaseImageList } from '@/api/caseImages'
import { getOrder as getOrderApi } from '@/api/orders'
import { getOrderProducts } from '@/api/orderProducts'
import { orderProductsSync } from '@/utils/global/orderProductsSync'

export const PrintPackageNav = () => {
  const handleError = useErrorHandler('PrintPackageNav')
  const [selectedProducts, setSelectedProducts] =
    useRecoilState(selectedProductsAtom)
  const [selectedTheme, setSelectedTheme] = useRecoilState(selectedThemeAtom)
  const customization = useRecoilValue(customizationAtom)
  const [order, setOrder] = useRecoilState(orderAtom)
  const setThemes = useSetRecoilState(themesAtom)
  const setPinnedThemes = useSetRecoilState(pinnedThemesAtom)
  const setCustomThemes = useSetRecoilState(customThemesAtom)
  const setProducts = useSetRecoilState(productsAtom)
  const setCase = useSetRecoilState(caseAtom)
  const setCaseImages = useSetRecoilState(caseImagesAtom)
  const setApplicationType = useSetRecoilState(applicationTypeAtom)
  const setThemesArray = useSetRecoilState(themesArrayAtom)
  const [currentProductId, setCurrentProductId] =
    useRecoilState(currentProductIdAtom)
  const applicationType = useRecoilValue(applicationTypeAtom)
  const { t } = useTranslation()

  const navigate = useNavigate()
  const { pathname: location, search } = useLocation()

  // Set application type and current product based on query params
  // Only set currentProductId from URL on initial load, don't override user selection
  useEffect(() => {
    const searchParams = new URLSearchParams(search)
    const m3OrderProductId = searchParams.get('m3OrderProductId')

    if (m3OrderProductId) {
      setApplicationType(M3)
      // Only set currentProductId if we don't have one stored yet
      // This preserves the user's selection when navigating back from editor
      if (!currentProductId) {
        setCurrentProductId(m3OrderProductId)
      }
    } else {
      setApplicationType(MDL)
    }
  }, [search, setApplicationType, setCurrentProductId, currentProductId])

  // Fetch global data once on component mount (except themes, which depend on order's selected products)
  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        const [
          { data: productsData },
          { data: pinnedThemesData },
          { data: customThemesData },
        ] = await Promise.all([
          getAllProducts(),
          getPinnedThemes(),
          getCustomThemes(),
        ])
        setProducts(productsData)
        setPinnedThemes(pinnedThemesData)
        setCustomThemes(customThemesData)
      } catch (error) {
        handleError(error?.response?.data?.message || error.message, error)
      }
    }
    fetchGlobalData()
    //eslint-disable-next-line
  }, [])

  // Use ref for handleError to prevent infinite loops in useCallback dependencies
  const handleErrorRef = useRef(handleError)
  handleErrorRef.current = handleError

  // Fetch themes function - extracted to be reusable
  const fetchThemes = useCallback(async () => {
    // Wait for order to be loaded
    if (!order?.id) return

    try {
      // Backend will determine application type filter based on selected order products
      const { data: themesData } = await getThemeCategories({
        orderId: order.id,
      })
      setThemes(themesData)
    } catch (error) {
      handleErrorRef.current(
        error?.response?.data?.message || error.message,
        error,
      )
    }
  }, [order?.id, setThemes])

  // Fetch themes on initial load when order is available
  useEffect(() => {
    fetchThemes()
  }, [fetchThemes])

  // Latest order/appType/fetchThemes via refs so the sync subscription below
  // is registered exactly ONCE — re-subscribing on every order/appType change
  // opens windows where a notify fires with no listener attached and a
  // stale-data broadcast is silently lost.
  const orderRef = useRef(order)
  orderRef.current = order
  const applicationTypeRef = useRef(applicationType)
  applicationTypeRef.current = applicationType
  const fetchThemesRef = useRef(fetchThemes)
  fetchThemesRef.current = fetchThemes

  // Subscribe to order products sync events to refresh themes and products after DB writes
  useEffect(() => {
    const unsubscribe = orderProductsSync.subscribe(async (data) => {
      const currentOrder = orderRef.current
      if (data?.products) {
        setSelectedProducts(data.products)
      } else if (data?.staleOrderId && data.staleOrderId === currentOrder?.id) {
        // Backend flagged our order data as stale (theme self-heal): refetch
        // from the source of truth so per-product theme_ids are current.
        try {
          const requestedOrderId = currentOrder.id
          const { data: products } = await getOrderProducts(
            requestedOrderId,
            currentOrder.case_id,
            applicationTypeRef.current,
          )
          // Drop the result if the user switched orders while the request
          // was in flight — the prior order's products must not overwrite
          // the new order's selection.
          if (orderRef.current?.id !== requestedOrderId) return
          setSelectedProducts(products)
        } catch (error) {
          handleErrorRef.current(
            error?.response?.data?.message || error.message,
            error,
          )
        }
      }
      fetchThemesRef.current()
    })
    return unsubscribe
  }, [setSelectedProducts])

  const getOrder = async () => {
    // Check if order is empty or doesn't have an id
    const noOrderIdOrEmptyOrder = !order.id || Object.keys(order).length === 0
    if (noOrderIdOrEmptyOrder) {
      try {
        const [, , order_id] = location.split('/')

        // Check for m3OrderProductId in query params (for M3 application type)
        const searchParams = new URLSearchParams(search)
        const m3OrderProductId = searchParams.get('m3OrderProductId')

        // Build params object if m3OrderProductId exists
        const params = m3OrderProductId ? { m3OrderProductId } : {}

        // First get the order
        const { data: fetchedOrder } = await getOrderApi(order_id, params)

        setOrder(fetchedOrder)
      } catch (error) {
        handleError(error?.response?.data?.message || error.message, error)
      }
    }
  }

  const getCase = async () => {
    try {
      const orderHasCase = order && order.case_id
      if (orderHasCase) {
        const searchParams = new URLSearchParams(search)
        const m3OrderProductId = searchParams.get('m3OrderProductId')
        const isM3 = applicationType === M3

        const { data: caseData } = await getCaseApi(
          order.case_id,
          isM3,
          order.id,
        )

        setCase(caseData)

        // Hydrate case images for downstream consumers (PageImage, ProductPrint).
        // caseImages live in a separate table from case as of the MIO refactor, so
        // a fresh load / refresh on /package/{id}/customize must fetch them here.
        // On failure, clear the atom so a stale list from a previously-opened
        // order can't bleed into the new one (mirrors Auth.js).
        try {
          const caseImages = await getCaseImageList(caseData.id)
          setCaseImages(caseImages)
        } catch (err) {
          setCaseImages([])
          console.warn('Failed to load case images', {
            caseId: caseData.id,
            error: err,
          })
        }

        // Both M3 and MDL now support per-product themes via getCaseById
        if (caseData.themes && caseData.themes.length > 0) {
          setThemesArray(caseData.themes)

          // Set current product ID from URL params only if not already set
          if (isM3 && m3OrderProductId && !currentProductId) {
            setCurrentProductId(m3OrderProductId)
          }

          // Always hydrate from the order's actual theme to avoid stale atom values
          setSelectedTheme(caseData.themes[0])
        } else if (caseData.theme) {
          setSelectedTheme(caseData.theme)
          setThemesArray([caseData.theme])
        }
      }
    } catch (error) {
      handleError(error?.response?.data?.message || error.message, error)
    }
  }

  useEffect(() => {
    getOrder()
    //eslint-disable-next-line
  }, [location])

  useEffect(() => {
    if (order.id) {
      getCase()
    }
    //eslint-disable-next-line
  }, [order])

  const pages = useMemo(() => {
    const [, , currentPath] = location.split('/')
    const baseUrl = `/package/${currentPath}`
    const pageArr = [
      `${baseUrl}/products`,
      `${baseUrl}/theme`,
      `${baseUrl}/customize`,
    ]
    return pageArr
  }, [location])

  const locationIdx = useMemo(() => {
    return pages.indexOf(location)
  }, [location, pages])

  const next = useMemo(() => {
    return pages[locationIdx + 1]
  }, [locationIdx, pages])

  const back = useMemo(() => {
    return pages[locationIdx - 1]
  }, [locationIdx, pages])

  const currentStep = useMemo(() => {
    switch (locationIdx) {
      case 0: // Products page
        if (selectedProducts.length > 0) {
          return 2 // Can go to Themes
        } else {
          return 1 // Must select products
        }
      case 1: // Theme page
        if (selectedTheme) {
          return 3 // Can go to Customize
        } else {
          return 2 // Must select a theme
        }
      case 2: // Customize page
        if (customization) {
          // Assuming customization indicates ability to finish
          return 4
        } else {
          return 3
        }
      default:
        return 0
    }
  }, [selectedProducts, selectedTheme, customization, locationIdx])

  const returnToDashboard = () => {
    setSelectedTheme(null)
    setSelectedProducts([])
    setOrder(null)
    setCase(null)
    navigate(`/dashboard`)
  }

  // Designers work a single scoped line and QC reviews a fixed order —
  // hide the step navigation UI (products / theme restaging) for both, but
  // only after every hook above has run: this component also loads the
  // order the rest of the page renders from.
  if (isDesignerTokenSession() || isQcTokenSession()) return null

  return (
    <DesignerGate>
      <Flex center gap="16px">
        <Button outline onClick={() => returnToDashboard()}>
          {t('package.saveAndExitButton')}
        </Button>
        <LogoutButton />
      </Flex>
      <Flex>
        <NavItem
          text={t('package.navigationSteps.products')}
          link={pages[0]}
          selected={location === pages[0]}
          disabled={false} // Products page should always be accessible initially
        />
        <NavItem
          text={t('package.navigationSteps.theme')}
          link={pages[1]}
          selected={location === pages[1]}
          disabled={currentStep < 2} // Disable if products not selected
        />
        <NavItem
          text={t('package.navigationSteps.customize')}
          link={pages[2]}
          selected={location === pages[2]}
          disabled={currentStep < 3} // Disable if theme not selected
        />
      </Flex>
      <Flex center gap="16px">
        {location !== pages[0] && (
          <Link href={back}>
            <Button
              outline
              iconLeft="chevronLeft"
              data-testid="package-back-button"
            >
              {t('package.backButton')}
            </Button>
          </Link>
        )}
        {location !== pages[2] && (
          <Link href={next} disabled={locationIdx + 1 >= currentStep}>
            <Button
              primary
              icon="chevronRight"
              data-testid="package-next-button"
            >
              {t('package.nextButton')}
            </Button>
          </Link>
        )}
        {location === pages[2] && (
          <Button primary icon="check" onClick={() => returnToDashboard()}>
            {t('package.finishButton')}
          </Button>
        )}
        {applicationType !== M3 && <LanguageSelector whiteIcon />}
      </Flex>
    </DesignerGate>
  )
}

export default PrintPackageNav
